import api, { ApiResponse } from './api';
import type { BadgeTone } from '@safetyhub/ui';
import {
  LEGAL_OBLIGATION_INSTANCE_RESOURCE_TYPE,
  LEGAL_OBLIGATION_SITE_RESOURCE_TYPE,
  legalObligationInstancePath,
  legalSiteObligationsPath,
} from './legalDocumentRoutes';
import { AGENCY_LINK_REQUEST_RESOURCE_TYPE, agencyLinkRequestPath } from './agencySiteLinkView';

// Types
/**
 * 서버 Prisma `NotificationType` enum과 1:1로 맞춘다.
 * 여기서 빠진 값은 배지에 라벨 대신 중립 문구가 나가므로,
 * enum에 값을 추가하면 이 union·라벨 맵·tone 맵 셋을 함께 늘려야 한다.
 */
export type NotificationType =
  // 보고서 관련
  | 'REPORT_SIGNATURE_REQUEST'
  | 'REPORT_SIGNATURE_REMINDER'
  | 'REPORT_ALL_SIGNED'
  | 'REPORT_CREATED'
  | 'REPORT_DELETED'
  | 'REPORT_SUBMITTED'
  | 'REPORT_SUBMISSION_CANCELLED'
  // 결재 관련
  | 'APPROVAL_REQUEST'
  | 'APPROVAL_COMPLETED'
  | 'APPROVAL_REJECTED'
  // 결재란 담당자를 찾을 수 없어 다음 단계가 대기 중 — 현장 배정으로 고친다 (park-and-resume)
  | 'APPROVAL_SLOT_UNRESOLVED'
  // 대기하던 결재란이 배정으로 해소돼 결재가 재개됨 (park-and-resume)
  | 'APPROVAL_SLOT_RESUMED'
  // 보고서 삭제 요청 관련
  | 'REPORT_DELETE_REQUESTED'
  | 'REPORT_DELETE_RETARGETED'
  | 'REPORT_DELETE_REQUEST_INVALIDATED'
  // 멤버/조직 관련
  | 'MEMBER_INVITED'
  | 'MEMBER_JOINED'
  | 'MEMBER_LEFT'
  | 'MEMBER_ROLE_CHANGED'
  | 'ORGANIZATION_RESTORED'
  // 현장 관련
  | 'SITE_ASSIGNED'
  | 'SITE_UNASSIGNED'
  | 'SITE_ASSIGNMENT_CHANGED'
  | 'SITE_UPDATED'
  | 'SITE_PRIMARY_NEEDED'
  // 대행 현장 연결(P4) — 연결·해제는 resourceType `site`, 요청·처리는 `site_agency_link_request`
  | 'SITE_AGENCY_LINKED'
  | 'SITE_AGENCY_UNLINKED'
  | 'SITE_AGENCY_LINK_REQUESTED'
  | 'SITE_AGENCY_LINK_RESOLVED'
  // 점검 관련
  | 'INSPECTION_CREATED'
  | 'INSPECTION_COMPLETED'
  | 'INSPECTION_MODIFIED'
  | 'INSPECTION_DELETED'
  | 'INSPECTION_RESTORED'
  | 'INSPECTION_CONTRIBUTOR_JOINED'
  | 'INSPECTION_REVIEW_REQUESTED'
  | 'INSPECTION_COMPLETION_REQUESTED'
  // 점검 스케줄 관련
  | 'INSPECTION_SCHEDULED'
  | 'INSPECTION_SCHEDULE_REMINDER'
  | 'INSPECTION_OVERDUE'
  // 스케줄 변경 요청 관련
  | 'SCHEDULE_CHANGE_REQUESTED'
  | 'SCHEDULE_CHANGE_APPROVED'
  | 'SCHEDULE_CHANGE_REJECTED'
  | 'SCHEDULE_CHANGE_REMINDER'
  | 'SCHEDULE_CHANGE_NO_RESPONSE'
  // 초대 관련
  | 'INVITATION_CANCELLED'
  | 'INVITATION_REJECTED'
  // AI 분석 관련
  | 'AI_ANALYSIS_STARTED'
  | 'AI_ANALYSIS_COMPLETED'
  | 'AI_ANALYSIS_FAILED'
  // PDF 관련
  | 'PDF_GENERATED'
  // TBM 관련
  | 'TBM_EDUCATION_NOT_STARTED'
  // 기관 인증 관련
  | 'VERIFICATION_COMPLETED'
  | 'VERIFICATION_MISMATCH'
  // 현장 이관 관련
  | 'SITE_TRANSFER_REQUEST'
  | 'SITE_TRANSFER_ACCEPTED'
  | 'SITE_TRANSFER_REJECTED'
  // DLR 위임 관련
  | 'DLR_DRAFT_COMPLETED'
  // 위임 반려 릴레이 (담당자에게 나가는 문구는 위임자가 쓴다)
  | 'DELEGATION_RELAY_NEEDED'
  | 'DELEGATION_CONTACT_NO_RESPONSE'
  | 'DELEGATION_DISPATCH_FAILED'
  | 'DELEGATION_CONTACT_REMOVED'
  // 계정/신원 관련
  | 'PHONE_REMOVED'
  // 법정 서류 관리 (줄기 항목 3 — 주기 작업)
  //
  // ⛔ **이 union 은 서버 Prisma enum 의 «사본»이라 tsc 가 누락을 잡지 못한다**(이 파일 머리의
  // 「1:1로 맞춘다」가 그 뜻이다). 그리고 아래 `labels`·`tones` 는 폴백을 가져
  // (`labels[type] || '알림'` · `tones[type] || 'slate'`) 빠진 타입이 **「알림」으로 조용히
  // degrade** 한다 ⇒ red 없이 낡는다. 서버에 알림 타입을 더하는 커밋은 **이 세 자리**
  // (union · labels · tones)와 `stores/notification.ts` 둘, `NotificationDropdown.tsx` 하나를
  // 함께 봐야 한다.
  | 'LEGAL_OBLIGATION_DUE_SOON'
  | 'LEGAL_OBLIGATION_EVENT_REVOKED'
  | 'LEGAL_OBLIGATION_AGENCY_CLOSING'
  // 관리자 메시지
  | 'ADMIN_MESSAGE'
  // 거래처 정보 병합 (정본 흡수 — 출신 조직 통지)
  | 'COMPANY_MERGED'
  // 테넌트 통합 (P4-B 동의 흐름)
  | 'COMPANY_MERGE_REQUEST'
  | 'COMPANY_MERGE_ACCEPTED'
  | 'COMPANY_MERGE_REJECTED';

export interface Notification {
  id: string;
  userId: string;
  companyId?: string;
  type: NotificationType;
  title: string;
  message: string;
  resourceType?: string;
  resourceId?: string;
  actionUrl?: string;
  metadata?: Record<string, string>;
  isRead: boolean;
  readAt?: string;
  createdAt: string;
}

export interface NotificationsListResponse {
  data: Notification[];
  meta: {
    total: number;
    limit: number;
    offset: number;
    unreadCount: number;
  };
}

export interface NotificationsFilters {
  unreadOnly?: boolean;
  limit?: number;
  offset?: number;
}

// API Functions

/**
 * 알림 목록 조회
 */
export async function getNotifications(
  filters?: NotificationsFilters
): Promise<NotificationsListResponse> {
  const params = new URLSearchParams();

  if (filters?.unreadOnly) params.append('unreadOnly', 'true');
  if (filters?.limit) params.append('limit', String(filters.limit));
  if (filters?.offset) params.append('offset', String(filters.offset));

  const response = await api.get<ApiResponse<Notification[]> & { meta: NotificationsListResponse['meta'] }>(
    `/notifications?${params.toString()}`
  );

  return {
    data: response.data.data || [],
    meta: response.data.meta || { total: 0, limit: 20, offset: 0, unreadCount: 0 },
  };
}

/**
 * 읽지 않은 알림 개수 조회
 */
export async function getUnreadCount(): Promise<number> {
  const response = await api.get<ApiResponse<{ count: number }>>('/notifications/unread-count');
  return response.data.data?.count || 0;
}

/**
 * 알림 읽음 처리
 */
export async function markAsRead(notificationId: string): Promise<Notification | null> {
  const response = await api.patch<ApiResponse<Notification>>(
    `/notifications/${notificationId}/read`
  );
  return response.data.data || null;
}

/**
 * 모든 알림 읽음 처리
 */
export async function markAllAsRead(): Promise<void> {
  await api.patch('/notifications/read-all');
}

/**
 * 모든 알림 삭제
 */
export async function clearAllNotifications(): Promise<{ count: number }> {
  const response = await api.delete<ApiResponse<{ count: number }>>('/notifications');
  return response.data.data || { count: 0 };
}

// Helpers

/**
 * 알림 타입 라벨
 */
export function getNotificationTypeLabel(type: NotificationType): string {
  const labels: Record<NotificationType, string> = {
    // 보고서 관련
    REPORT_SIGNATURE_REQUEST: '서명 요청',
    REPORT_SIGNATURE_REMINDER: '서명 리마인더',
    REPORT_ALL_SIGNED: '서명 완료',
    REPORT_CREATED: '보고서 생성',
    REPORT_DELETED: '보고서 삭제',
    REPORT_SUBMITTED: '보고서 제출',
    REPORT_SUBMISSION_CANCELLED: '제출 취소',
    // 결재 관련
    APPROVAL_REQUEST: '결재 요청',
    APPROVAL_COMPLETED: '결재 완료',
    APPROVAL_REJECTED: '결재 반려',
    APPROVAL_SLOT_UNRESOLVED: '결재란 담당자 배정 필요',
    APPROVAL_SLOT_RESUMED: '결재 재개',
    // 보고서 삭제 요청 관련
    REPORT_DELETE_REQUESTED: '삭제 승인 요청',
    REPORT_DELETE_RETARGETED: '삭제 요청 재전달',
    REPORT_DELETE_REQUEST_INVALIDATED: '삭제 요청 무효화',
    // 멤버/조직 관련
    MEMBER_INVITED: '조직 초대',
    MEMBER_JOINED: '멤버 합류',
    MEMBER_LEFT: '멤버 탈퇴',
    MEMBER_ROLE_CHANGED: '역할 변경',
    ORGANIZATION_RESTORED: '조직 복원',
    // 현장 관련
    SITE_ASSIGNED: '현장 배정',
    SITE_UNASSIGNED: '현장 배정 해제',
    SITE_ASSIGNMENT_CHANGED: '담당자 유형 변경',
    SITE_UPDATED: '현장 수정',
    SITE_PRIMARY_NEEDED: '정 담당자 필요',
    SITE_AGENCY_LINKED: '대행 기관 연결',
    SITE_AGENCY_UNLINKED: '대행 연결 해제',
    SITE_AGENCY_LINK_REQUESTED: '대행 연결 요청',
    SITE_AGENCY_LINK_RESOLVED: '대행 연결 요청 처리',
    // 점검 관련
    INSPECTION_CREATED: '점검 생성',
    INSPECTION_COMPLETED: '점검 완료',
    INSPECTION_MODIFIED: '점검 수정',
    INSPECTION_DELETED: '점검 삭제',
    INSPECTION_RESTORED: '점검 복원',
    INSPECTION_CONTRIBUTOR_JOINED: '점검 참여',
    INSPECTION_REVIEW_REQUESTED: '검토 요청',
    INSPECTION_COMPLETION_REQUESTED: '점검 완료 서명 요청',
    // 점검 스케줄 관련
    INSPECTION_SCHEDULED: '점검 예약',
    INSPECTION_SCHEDULE_REMINDER: '점검 리마인더',
    INSPECTION_OVERDUE: '점검 기한 초과',
    // 스케줄 변경 요청 관련
    SCHEDULE_CHANGE_REQUESTED: '일정 변경 요청',
    SCHEDULE_CHANGE_APPROVED: '일정 변경 승인',
    SCHEDULE_CHANGE_REJECTED: '일정 변경 거부',
    SCHEDULE_CHANGE_REMINDER: '일정 변경 리마인더',
    SCHEDULE_CHANGE_NO_RESPONSE: '일정 변경 미응답',
    // 초대 관련
    INVITATION_CANCELLED: '초대 취소',
    INVITATION_REJECTED: '초대 거절',
    // AI 분석 관련
    AI_ANALYSIS_STARTED: '분석 시작',
    AI_ANALYSIS_COMPLETED: '분석 완료',
    AI_ANALYSIS_FAILED: '분석 실패',
    // PDF 관련
    PDF_GENERATED: 'PDF 생성 완료',
    // TBM 관련
    TBM_EDUCATION_NOT_STARTED: 'TBM 교육 미진행',
    // 기관 인증 관련
    VERIFICATION_COMPLETED: '기관 인증 완료',
    VERIFICATION_MISMATCH: '사업자 정보 불일치',
    // 현장 이관 관련
    SITE_TRANSFER_REQUEST: '현장 이관 요청',
    SITE_TRANSFER_ACCEPTED: '현장 이관 수락',
    SITE_TRANSFER_REJECTED: '현장 이관 거부',
    // DLR 위임 관련
    DLR_DRAFT_COMPLETED: 'DLR 작성 완료',
    // 위임 반려 릴레이
    DELEGATION_RELAY_NEEDED: '담당자 재요청 필요',
    DELEGATION_CONTACT_NO_RESPONSE: '담당자 미응답',
    DELEGATION_DISPATCH_FAILED: '담당자 발송 실패',
    DELEGATION_CONTACT_REMOVED: '위임 담당자 변경 필요',
    // 계정/신원 관련
    PHONE_REMOVED: '번호 해제',
    // 법정 서류 — ⚠️ 「해당 없음」류 문구를 쓰지 않는다(그 말은 선임 안내·수동 제외와 세 뜻으로 갈린다)
    LEGAL_OBLIGATION_DUE_SOON: '법정 서류 기한 임박',
    LEGAL_OBLIGATION_EVENT_REVOKED: '법정 서류 사건 등록 취소',
    LEGAL_OBLIGATION_AGENCY_CLOSING: '법정 서류 대행 기관 탈퇴 예정',
    // 관리자 메시지
    ADMIN_MESSAGE: '운영팀 공지',
    // 거래처 정보 병합
    COMPANY_MERGED: '거래처 정보 병합',
    // 테넌트 통합
    COMPANY_MERGE_REQUEST: '조직 통합 요청',
    COMPANY_MERGE_ACCEPTED: '조직 통합 수락',
    COMPANY_MERGE_REJECTED: '조직 통합 거절',
  };
  // 서버가 아직 여기 없는 enum을 보내도 배지에 raw enum을 노출하지 않는다.
  return labels[type] || '알림';
}

/**
 * 알림 타입 → 공용 Badge tone. 색 축의 단일 정본이다 —
 * 배지는 이 tone을 그대로 쓰고, 아이콘 원형은 아래 `getNotificationTypeColor`가 파생한다.
 */
export function getNotificationTypeBadgeTone(type: NotificationType): BadgeTone {
  const tones: Record<NotificationType, BadgeTone> = {
    // 보고서 관련
    REPORT_SIGNATURE_REQUEST: 'blue',
    REPORT_SIGNATURE_REMINDER: 'amber',
    REPORT_ALL_SIGNED: 'emerald',
    REPORT_CREATED: 'emerald',
    REPORT_DELETED: 'rose',
    REPORT_SUBMITTED: 'blue',
    REPORT_SUBMISSION_CANCELLED: 'amber',
    // 결재 관련
    APPROVAL_REQUEST: 'violet',
    APPROVAL_COMPLETED: 'emerald',
    APPROVAL_REJECTED: 'rose',
    APPROVAL_SLOT_UNRESOLVED: 'amber',
    // 재개는 "막힌 것이 풀렸다"는 좋은 소식 — 결재 완료(emerald) 계열을 잇는다.
    APPROVAL_SLOT_RESUMED: 'emerald',
    // 보고서 삭제 요청 관련
    REPORT_DELETE_REQUESTED: 'amber',
    REPORT_DELETE_RETARGETED: 'amber',
    REPORT_DELETE_REQUEST_INVALIDATED: 'slate',
    // 멤버/조직 관련
    MEMBER_INVITED: 'blue',
    MEMBER_JOINED: 'emerald',
    MEMBER_LEFT: 'slate',
    MEMBER_ROLE_CHANGED: 'violet',
    ORGANIZATION_RESTORED: 'emerald',
    // 현장 관련
    SITE_ASSIGNED: 'blue',
    SITE_UNASSIGNED: 'slate',
    SITE_ASSIGNMENT_CHANGED: 'violet',
    SITE_UPDATED: 'blue',
    SITE_PRIMARY_NEEDED: 'amber',
    // 대행 연결 — 요청은 업체 소유자가 승인·거절할 **할 일**이라 amber, 해제는 이미 끝난 안내라 slate
    SITE_AGENCY_LINKED: 'blue',
    SITE_AGENCY_UNLINKED: 'slate',
    SITE_AGENCY_LINK_REQUESTED: 'amber',
    SITE_AGENCY_LINK_RESOLVED: 'blue',
    // 점검 관련
    INSPECTION_CREATED: 'blue',
    INSPECTION_COMPLETED: 'emerald',
    INSPECTION_MODIFIED: 'amber',
    INSPECTION_DELETED: 'rose',
    INSPECTION_RESTORED: 'emerald',
    INSPECTION_CONTRIBUTOR_JOINED: 'blue',
    INSPECTION_REVIEW_REQUESTED: 'violet',
    INSPECTION_COMPLETION_REQUESTED: 'violet',
    // 점검 스케줄 관련
    INSPECTION_SCHEDULED: 'blue',
    INSPECTION_SCHEDULE_REMINDER: 'amber',
    INSPECTION_OVERDUE: 'rose',
    // 스케줄 변경 요청 관련
    SCHEDULE_CHANGE_REQUESTED: 'violet',
    SCHEDULE_CHANGE_APPROVED: 'emerald',
    SCHEDULE_CHANGE_REJECTED: 'rose',
    SCHEDULE_CHANGE_REMINDER: 'amber',
    SCHEDULE_CHANGE_NO_RESPONSE: 'amber',
    // 초대 관련
    INVITATION_CANCELLED: 'slate',
    INVITATION_REJECTED: 'rose',
    // AI 분석 관련
    AI_ANALYSIS_STARTED: 'blue',
    AI_ANALYSIS_COMPLETED: 'emerald',
    AI_ANALYSIS_FAILED: 'rose',
    // PDF 관련
    PDF_GENERATED: 'emerald',
    // TBM 관련
    TBM_EDUCATION_NOT_STARTED: 'amber',
    // 기관 인증 관련
    VERIFICATION_COMPLETED: 'emerald',
    VERIFICATION_MISMATCH: 'amber',
    // 현장 이관 관련
    SITE_TRANSFER_REQUEST: 'amber',
    SITE_TRANSFER_ACCEPTED: 'emerald',
    SITE_TRANSFER_REJECTED: 'rose',
    // DLR 위임 관련
    DLR_DRAFT_COMPLETED: 'blue',
    // 위임 반려 릴레이 — 앞 둘은 위임자가 **할 일이 남은** 축이라 amber,
    // 발송 실패는 이미 잘못된 축이라 rose
    DELEGATION_RELAY_NEEDED: 'amber',
    DELEGATION_CONTACT_NO_RESPONSE: 'amber',
    DELEGATION_DISPATCH_FAILED: 'rose',
    // 담당자 신원 소실 — 링크는 이미 죽었고 위임자가 재지정해야 하는 **할 일** 축이라 amber
    DELEGATION_CONTACT_REMOVED: 'amber',
    // 계정/신원 관련 — 조치가 필요한 경고 축이라 amber
    PHONE_REMOVED: 'amber',
    // 법정 서류 기한 임박 — 기한이 남아 있는 **할 일** 축이라 amber(넘긴 뒤의 `OVERDUE`는 별 축이고
    // 아직 타입이 없다 — 그것이 서면 rose 가 맞다)
    LEGAL_OBLIGATION_DUE_SOON: 'amber',
    // 사건 등록 취소 — 할 일이 **사라졌다**는 안내라 조치 축(amber)이 아니다
    LEGAL_OBLIGATION_EVENT_REVOKED: 'slate',
    // 대행 기관 탈퇴 예정(L32 결정 ①) — 유예 안에 기록을 내려받는 조치가 필요하다(조치 축 amber)
    LEGAL_OBLIGATION_AGENCY_CLOSING: 'amber',
    // 관리자 메시지
    ADMIN_MESSAGE: 'brand',
    // 거래처 정보 병합
    COMPANY_MERGED: 'blue',
    // 테넌트 통합
    COMPANY_MERGE_REQUEST: 'amber',
    COMPANY_MERGE_ACCEPTED: 'emerald',
    COMPANY_MERGE_REJECTED: 'rose',
  };
  return tones[type] || 'slate';
}

/**
 * 알림 타입별 아이콘 원형 색상(면 + 글자).
 * Tailwind 정적 스캔 대상이라 완성형 리터럴 맵으로 둔다(동적 조합 금지).
 */
export function getNotificationTypeColor(type: NotificationType): string {
  const iconClass: Record<BadgeTone, string> = {
    blue: 'text-blue-600 bg-blue-100',
    emerald: 'text-emerald-600 bg-emerald-100',
    amber: 'text-amber-600 bg-amber-100',
    orange: 'text-orange-600 bg-orange-100',
    rose: 'text-rose-600 bg-rose-100',
    violet: 'text-violet-600 bg-violet-100',
    teal: 'text-teal-600 bg-teal-100',
    slate: 'text-ink-600 bg-ink-100',
    brand: 'text-brand-600 bg-brand-100',
  };
  return iconClass[getNotificationTypeBadgeTone(type)];
}

/**
 * 알림에서 리소스 경로 반환
 */
export function getNotificationResourcePath(notification: Notification): string | null {
  // actionUrl이 있으면 우선 사용 (결재 요청 등 직접 액션 URL)
  if (notification.actionUrl) {
    return notification.actionUrl;
  }

  // 현장 이관 요청: metadata.token으로 이관 수락 페이지 이동
  if (notification.type === 'SITE_TRANSFER_REQUEST') {
    const token = notification.metadata?.token;
    if (token) return `/site-transfers/${token}`;
    return null;
  }

  // 조직 통합 요청: 대상 조직 OWNER가 열 수락 화면. resourceId가 토큰이다.
  // 수락/거절 통지(요청 조직 쪽 수신)는 같은 토큰을 달고 오지만 그때 요청은 이미 PENDING이
  // 아니라 수락 화면이 열리지 않는다 — 진행 상태를 보는 자리인 조직 설정으로 보낸다.
  if (notification.type === 'COMPANY_MERGE_REQUEST' && notification.resourceId) {
    return `/company-merge/${notification.resourceId}`;
  }
  if (
    notification.type === 'COMPANY_MERGE_ACCEPTED' ||
    notification.type === 'COMPANY_MERGE_REJECTED'
  ) {
    return '/settings/organization';
  }

  if (!notification.resourceType || !notification.resourceId) return null;

  switch (notification.resourceType.toLowerCase()) {
    case 'report':
      return `/reports/${notification.resourceId}`;
    case 'inspection':
      return `/inspections/${notification.resourceId}`;
    case 'approval':
      return `/approvals/${notification.resourceId}`;
    // 현장 — 헤더 벨·실시간 토스트·SW에는 있었고 이 목록만 빠져 있었다(대행 연결·해제 알림 `SITE_AGENCY_LINKED`·`…_UNLINKED`가 이 타입)
    case 'site':
      return `/sites/${notification.resourceId}`;
    // 대행 연결 요청·처리 — 경로 정본은 `lib/agencySiteLinkView.ts`(벨·토스트·SW와 같은 함수)
    case AGENCY_LINK_REQUEST_RESOURCE_TYPE:
      return agencyLinkRequestPath(notification.resourceId);
    // 법정 서류 기한 임박 — 헤더 벨(`NotificationDropdown`)과 같은 경로 함수(`lib/legalDocumentRoutes.ts`)
    case LEGAL_OBLIGATION_INSTANCE_RESOURCE_TYPE:
      return legalObligationInstancePath(notification.resourceId);
    // 사건 등록 취소 — resourceId = 현장 id(인스턴스는 지워졌다) · 같은 경로 모듈
    case LEGAL_OBLIGATION_SITE_RESOURCE_TYPE:
      return legalSiteObligationsPath(notification.resourceId);
    default:
      return null;
  }
}

/**
 * 상대적 시간 포맷
 */
export function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return '방금 전';
  if (diffMin < 60) return `${diffMin}분 전`;
  if (diffHour < 24) return `${diffHour}시간 전`;
  if (diffDay < 7) return `${diffDay}일 전`;

  return date.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
